import { login, refreshToken, getUserInfo } from '@/api/auth'
import { getToken, setToken, removeToken, setRefreshToken, removeRefreshToken, getRefreshToken } from '@/utils/auth'
import { renewSession } from '@/utils/session'

const state = {
  token: getToken(),
  userInfo: null,
  tenantId: null,
  // 已授予的权限名集合，来自 ABP application-configuration 的 auth.grantedPolicies
  permissions: {}
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_USER_INFO(state, info) {
    state.userInfo = info
  },
  SET_TENANT_ID(state, id) {
    state.tenantId = id
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions || {}
  }
}

const actions = {
  // 登录（OpenIddict password grant）
  async login({ commit }, loginForm) {
    const { data } = await login(loginForm)
    // OpenIddict 返回: { access_token, refresh_token, token_type, expires_in, scope }
    const accessToken = data.access_token
    const refreshTokenValue = data.refresh_token

    commit('SET_TOKEN', accessToken)
    setToken(accessToken)

    if (refreshTokenValue) {
      setRefreshToken(refreshTokenValue)
    }

    return data
  },

  // 刷新 Token
  async refreshToken({ commit }) {
    const currentRefreshToken = getRefreshToken()
    if (!currentRefreshToken) {
      const error = new Error('No refresh token')
      error.sessionExpired = true
      throw error
    }
    const { data } = await refreshToken(currentRefreshToken)
    commit('SET_TOKEN', data.access_token)
    setToken(data.access_token)
    if (data.refresh_token) {
      setRefreshToken(data.refresh_token)
    }
    return data
  },

  // 获取用户信息（从 ABP application-configuration 端点）
  async getInfo({ commit, state }) {
    // 使用 ABP 的配置端点获取当前用户信息
    const axios = (await import('axios')).default
    const loadInfo = () => axios.get('/api/abp/application-configuration', {
      headers: { Authorization: `Bearer ${state.token}` },
      timeout: 30000
    })
    let response
    let renewed = false
    try {
      response = await loadInfo()
    } catch (error) {
      if (!error.response || error.response.status !== 401) throw error
      await renewSession()
      renewed = true
      response = await loadInfo()
    }
    // ABP 配置端点允许匿名访问：过期凭据也可能返回 200 + 未认证用户。
    if (!response.data.currentUser?.isAuthenticated && !renewed) {
      await renewSession()
      response = await loadInfo()
    }
    const { data } = response
    const currentUser = data.currentUser
    if (currentUser && currentUser.isAuthenticated) {
      const userInfo = {
        id: currentUser.id,
        userName: currentUser.userName,
        name: currentUser.name,
        surName: currentUser.surName,
        email: currentUser.email,
        roles: currentUser.roles || []
      }
      commit('SET_USER_INFO', userInfo)
      // grantedPolicies 形如 { "CarbonManagement.Energy": true, ... }，
      // 只包含已授予的项，未授予的键直接不存在
      commit('SET_PERMISSIONS', (data.auth && data.auth.grantedPolicies) || {})
      return userInfo
    }
    const error = new Error('Not authenticated')
    error.sessionExpired = true
    throw error
  },

  // 退出
  async logout({ commit }) {
    commit('SET_TOKEN', null)
    commit('SET_USER_INFO', null)
    // 权限必须一并清空，否则换账号登录会短暂沿用上一个人的菜单
    commit('SET_PERMISSIONS', {})
    removeToken()
    removeRefreshToken()
  },

  // 设置租户
  setTenant({ commit }, tenantId) {
    commit('SET_TENANT_ID', tenantId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
